import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/shared/utils/logger';
import { ROUTES } from '@/shared/constants';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('[ErrorBoundary] Caught an error:', error, errorInfo);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message ?? 'An unexpected error occurred';
      const errorDetails = this.state.error?.stack;

      return (
        <div className="bg-screen flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-2xl">
            <div className="rounded-lg bg-white p-8 shadow-xl">
              <div className="bg-error-bg mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full">
                <svg
                  className="text-error h-8 w-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <title>Error Icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h1 className="text-high-emphasis mb-4 text-3xl font-bold">
                Oops! Something went wrong
              </h1>

              <p className="text-medium-emphasis mb-6">{errorMessage}</p>

              {/* biome-ignore lint/complexity/useLiteralKeys: TypeScript strict mode requires bracket notation for process.env */}
              {errorDetails && process.env['NODE_ENV'] === 'development' && (
                <details className="mb-6">
                  <summary className="text-low-emphasis hover:text-medium-emphasis mb-2 cursor-pointer text-sm">
                    Technical Details
                  </summary>
                  <pre className="bg-input max-h-64 overflow-auto rounded p-4 text-xs">
                    <code>{errorDetails}</code>
                  </pre>
                </details>
              )}

              <div className="flex justify-center space-x-4">
                {/* Using <a> instead of <Link> because ErrorBoundary is outside RouterProvider */}
                <a
                  href={ROUTES.HOME}
                  className="bg-primary hover:bg-primary inline-block rounded-lg px-6 py-3 font-medium text-white transition-colors"
                >
                  Go Home
                </a>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="bg-input text-high-emphasis hover:bg-input inline-block rounded-lg px-6 py-3 font-medium transition-colors"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
